import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {PortvisitUtils} from '../../../../refdata/portvisit-utils';
import {
  CommercialReleaseSummary,
  GetMyCarriers,
  IE3ConsignmentProcessSummary,
  ReleaseToParty
} from '@portbase/bezoekschip-service-typescriptmodels';
import {AppContext} from '../../../../app-context';
import {sendQuery} from '../../../../common/utils';
import {map} from 'rxjs/operators';
import {
  findReleaseToParties as findReleaseToPartiesShared,
  terminalOrganisationName,
  terminals
} from '../../../../commercial-release/commercial-release.utils';

/**
 * The modal's own "Parties" tab, matching the consignments Parties-tab visual structure
 * (`master-consignment-details.component.html`'s `container.py-3 > card > card-body.p-5 > row
 * gy-4 my-5 > h5.h1` shape). Holds the three parties the founder asked to move out of "Release
 * details" into their own tab: Release-to party, Container operator, Inland operator.
 *
 * Founder feedback (reverses an earlier decision): "De parties zijn helemaal niet hetzelfde zoals
 * bij de consignments" - a flat single-level `app-form-field` + `app-search` row does NOT match how
 * consignments actually render a party in display mode. The real precedent for a "thin" party (no
 * postal address, unlike Carrier/Consignee/Consignor) is `ConsignmentSupplementaryDeclarantComponent`:
 * a label line OUTSIDE any card, a `card > card-body` wrapping the field, and a per-party INNER
 * edit-mode toggle (pencil icon + Cancel/Save) that is entirely absent in pure display mode. Each of
 * the three parties here now needs that same inner-toggle state+backup+cancel/save skeleton, so -
 * per Rule 7 (don't duplicate the same 15+ line skeleton three times) - it is factored out into the
 * shared, thin `CommercialReleasePartyFieldComponent` (label+card+inner-toggle wrapper), with the
 * actual per-party lookup control (`app-search`, with its own formatter/searchFunction) projected in
 * as content. This keeps the wrapper generic across the three different entity types
 * (`ReleaseToParty`/`Carrier`/`InlandOperator`) without forking a full sub-component per party.
 *
 * Follow-up founder feedback: unlike Release-to party/Inland operator, Container operator is bound
 * to the generated `Carrier` type, which DOES carry a full postal address + EORI number (same shape
 * consignments' own Carrier field renders). `[richAddress]="true"` below opts Container operator
 * into `CommercialReleasePartyFieldComponent`'s richer read-only field grid instead of the single
 * collapsed formatted string the other two thin parties still use - see that component's class doc
 * for the full field-count rationale per party.
 *
 * `selectedConsignment` is passed down from `CommercialReleaseModalDetailsComponent` (via the modal
 * shell) purely so Container operator's CRN-linked lookup can scope results to the just-selected
 * consignment's carrier organisation, exactly like the original `app-select`'s `refreshWatch` did.
 */
/**
 * `ReleaseToParty` (the generated model) has no address slot. `FindCustomers` actually returns
 * `ApiCustomer` objects whose untyped `customerData?: any` genuinely carries `address`/`zipCode`/
 * `city` at runtime - confirmed via the real mock fixture (`bezoekschip-query-handler.mock.ts`) and
 * the unrelated `ReleaseToPartyOverviewComponent`'s own Excel export, which already reads the exact
 * same query's `customerData.zipCode`/`.city` fields. This local interface (same
 * `extends`-the-generated-model pattern as that component's own `ApiCustomerUi extends ApiCustomer`)
 * lets `findReleaseToParties` carry that address data through to
 * `CommercialReleasePartyFieldComponent`'s `[basicAddress]` grid without forking `ReleaseToParty`
 * itself. The actual `FindCustomers` query + flatMap-over-references step now lives in the shared
 * `findReleaseToParties` util in `commercial-release.utils.ts` (reused by the new overview's
 * release-to-party facet filter) - this method only re-maps that shared result's carried-through
 * `customerData` onto the address fields this modal needs.
 */
interface ReleaseToPartyWithAddress extends ReleaseToParty {
  address?: string;
  zipCode?: string;
  city?: string;
}

@Component({
  selector: 'app-commercial-release-modal-parties',
  templateUrl: './commercial-release-modal-parties.component.html',
  styleUrls: ['./commercial-release-modal-parties.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CommercialReleaseModalPartiesComponent {
  refData = PortvisitUtils;
  appContext = AppContext;

  release = input.required<CommercialReleaseSummary>();
  editMode = input<boolean>(false);
  selectedConsignment = input<IE3ConsignmentProcessSummary>();

  // Matches the OLD screen's `carriersProvider` (`commercial-release-details.component.ts`) - the
  // founder explicitly asked for Container operator to become a bounded select once a consignment's
  // CRN is known, same as the old screen, backed by the same `GetMyCarriers` query. This REVERSES
  // part of an earlier decision that unified Container operator onto `app-search` in both branches
  // (see the no-CRN `@else` branch below, which still intentionally uses `app-search` for manual
  // entry) - the founder's later, explicit instruction takes precedence for the CRN-known branch
  // specifically. `app-select`'s `refreshProvider` is called with no search term (it fetches the
  // full option set once per `refreshWatch` change; `app-select` filters/renders client-side), so
  // unlike the old `findContainerOperatorsForConsignment` this does not filter by `term`.
  carriersProvider = computed(() => {
    const iamConnectedId = this.selectedConsignment()?.iamConnectedId
      || this.release().releaseData?.containerOperator?.cargoDeclarantShortName;
    return sendQuery(
      "com.portbase.bezoekschip.common.api.visit.GetMyCarriers", <GetMyCarriers>{
        cargoDeclarantIamConnectedId: iamConnectedId || AppContext.userProfile.organisation?.iamConnectedId
      });
  });

  findReleaseToParties = term => {
    const ownerShortName = (AppContext.isAdmin() && this.release().releaseData?.containerOperator?.cargoDeclarantShortName)
      || AppContext.userProfile.organisation?.shortName;
    return findReleaseToPartiesShared(term, ownerShortName)
      .pipe(map(results => results.map(r => <ReleaseToPartyWithAddress>{
        name: r.name,
        portbaseId: r.portbaseId,
        customerReference: r.customerReference,
        address: r.customerData?.address,
        zipCode: r.customerData?.zipCode,
        city: r.customerData?.city
      })));
  };
  protected readonly terminals = terminals;
  protected readonly terminalOrganisationName = terminalOrganisationName;
}
